/**
 * niftiDiscovery.ts
 *
 * Discover NIfTI files via the disk_items API.
 * Includes both MRI hierarchy logic and PET/SUV mask discovery logic.
 */

import {
  NIFTI_FILE_REGISTRY,
  BRAINVISA_HIERARCHY,
  NiftiFileEntry,
  Modality,
} from "./niftiRegistry";
import maskRegions from "../commons/maskRegions.json";

// ═══════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════

export interface DiscoveredFile {
  /** Matched registry entry */
  entry: NiftiFileEntry;
  /**  Full URL for loading */
  url: string;
  /** Raw path from disk_items API */
  rawPath: string;
  /**
   * Info extracted from directoryRegex capture groups
   * e.g.: { subject: "161_S16", acquisition: "ses-20231215" }
   */
  infos: Record<string, string>;
}

export interface DiscoveryResult {
  /** All available base images */
  bases: DiscoveredFile[];
  /** All available segmentations */
  segmentations: DiscoveredFile[];
  /** Currently active modality */
  activeModality: Modality;
  /** Quick lookup by setType */
  byKey: Map<string, DiscoveredFile>;
  /** All discovered files */
  allFiles: DiscoveredFile[];
  /** Extracted common infos */
  infos: Record<string, string>;
}

export interface PetImage {
  url: string;
  modality: string;
  label: string;
}

export interface PetMask {
  role?: "reference" | "interest" | "other";
  url: string;
  modality: string;
  label: string;
  color: [number, number, number];
}

// ═══════════════════════════════════════════════════════════════
//  PET Helpers
// ═══════════════════════════════════════════════════════════════

export const isPetModality = (modality?: string | null): boolean =>
  !!modality?.startsWith("pet-");

export function petTracerLabel(modality: string): string {
  return modality.replace(/^pet-/, "").replace(/[-_]/g, " ").toUpperCase();
}

const MASK_COLORS: [number, number, number][] = [
  [0, 220, 255],
  [255, 110, 160],
  [120, 230, 100],
  [255, 210, 70],
  [190, 140, 255],
  [255, 150, 60],
];

export function buildPetFiles(files: DiscoveredFile[]): {
  images: PetImage[];
  masks: PetMask[];
} {
  const images: PetImage[] = [];
  const masks: PetMask[] = [];
  for (const file of files) {
    if (!isPetModality(file.entry.modality)) continue;
    const modality = file.entry.modality!;
    const variant = file.infos.variant;
    if (file.entry.role === "base") {
      images.push({
        url: file.url,
        modality,
        label: `${petTracerLabel(modality)} — ${
          variant === "SUV" ? "SUV" : "PET"
        } (MNI)`,
      });
    } else if (file.entry.role === "segmentation") {
      // Stable color across discovery order, patients and tracers.
      const hash = Array.from(variant).reduce(
        (sum, c) => sum + c.charCodeAt(0),
        0,
      );
      // @ts-ignore
      const tracer = maskRegions.tracers[modality.replace(/^pet-/, "")];
      // @ts-ignore
      const region = maskRegions.regions[tracer]?.[variant];
      masks.push({
        url: file.url,
        modality,
        label: region?.label ?? variant.replace(/[-_]/g, " "),
        role: region?.role ?? "other",
        color:
          region?.role === "reference"
            ? [0, 220, 255]
            : region?.role === "interest"
            ? [255, 150, 60]
            : MASK_COLORS[hash % MASK_COLORS.length],
      });
    }
  }
  images.sort((a, b) => a.label.localeCompare(b.label));
  masks.sort((a, b) => a.label.localeCompare(b.label));
  return { images, masks };
}

// Paths produced by pet-quantification/src/config/*.json. Match these before
// the MRI hierarchy: its prefix patterns cannot distinguish PET from SUV.
export function matchPetFile(relativePath: string): {
  entry: NiftiFileEntry;
  infos: Record<string, string>;
} | null {
  const match = relativePath.match(
    /^(?<subject>[^/]+)\/(?:nii(?:\.gz)?\/)?(?<acquisition>ses-[^/]+)\/(?<modality>pet-[\w-]+)\/(?<directory>spm\/preprocessed|pet-quantification\/segmentation)\/normalized_space\/(?<filename>[^/]+)\.nii(?:\.gz)?$/,
  );
  if (!match) return null;
  const { subject, acquisition, modality, directory, filename } = match.groups!;
  let role: "base" | "segmentation";
  let variant: string;
  if (directory === "spm/preprocessed") {
    if (filename === `${subject}_space-MNI`) variant = "PET";
    else if (filename === `${subject}_space-MNI-suv`) variant = "SUV";
    else return null;
    role = "base";
  } else {
    const prefix = `${subject}_segm-`;
    if (!filename.startsWith(prefix) || filename.length === prefix.length)
      return null;
    variant = filename.slice(prefix.length);
    role = "segmentation";
  }
  return {
    entry: {
      setType: `PET:${modality}:${role}:${variant}`,
      directory,
      directoryRegex: /./,
      infos: ["subject", "acquisition", "modality", "variant"],
      pattern: filename,
      patternRegex: /./,
      role,
      modality: modality as `pet-${string}`,
      isViewerRelevant: true,
      roiGroups: [],
    },
    infos: { subject, acquisition, modality, variant },
  };
}

// ═══════════════════════════════════════════════════════════════
//  transformPath — ported from old project's DiskItems.jsx
// ═══════════════════════════════════════════════════════════════

export function transformPath(path: string): string {
  const extension = ["nii.gz", "nii", "json", "pdf"].find((ext) =>
    path.endsWith("." + ext),
  );
  if (!extension) return path;
  // Old project uses \d+/\d+/\d+, but subject folder may contain non-digits (e.g. 161_S16)
  return path
    .replace(/\/(\d+\/\d+\/[^/]+)\//, "/$1/" + extension + "/")
    .replace("." + extension, "");
}

// ═══════════════════════════════════════════════════════════════
//  fetchDiskItems — call the disk_items API
// ═══════════════════════════════════════════════════════════════

async function fetchDiskItems(
  baseUrl: string,
  patientId: string,
  acquisition: string,
): Promise<string[]> {
  const url = `${baseUrl}/patients/${patientId}/disk_items/${acquisition}`;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(
        "Timed out while finding images. Check the API port-forward.",
      );
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }

  if (!resp.ok) {
    throw new Error(
      `Failed to fetch disk items: ${resp.status} ${resp.statusText}`,
    );
  }

  const data = await resp.json();
  let files: string[] = [];
  if (Array.isArray(data)) files = data;
  else if (data?.items && Array.isArray(data.items)) files = data.items;
  else if (data?.disk_items && Array.isArray(data.disk_items))
    files = data.disk_items;
  else if (data?.files && Array.isArray(data.files)) files = data.files;

  return files;
}

function parseDiskItemType(relativePath: string): {
  infos: Record<string, string>;
  bv_type: string;
} | null {
  // Only process these formats
  if (
    ![".nii", ".nii.gz", ".json", ".pdf"].some((ext) =>
      relativePath.endsWith(ext),
    )
  ) {
    return null;
  }

  const chunks = relativePath.split("/");
  if (chunks.length < 2) {
    return null;
  }

  // Directory = all segments except last one (filename with extension)
  const directory = chunks.slice(0, chunks.length - 1).join("/");

  // Filename (with extension, since old code's patternRegex uses ^ to match filename)
  let pattern = chunks[chunks.length - 1];
  // Strip extension for matching (old code patterns don't include extensions)
  for (const ext of [".nii.gz", ".nii", ".json", ".pdf"]) {
    if (pattern.endsWith(ext)) {
      pattern = pattern.slice(0, -ext.length);
      break;
    }
  }

  // Iterate all hierarchy entries, dual regex match
  let candidate: string | null = null;

  for (const [bvType, typeInfo] of Object.entries(BRAINVISA_HIERARCHY)) {
    if (
      typeInfo.patternRegex.test(pattern) &&
      typeInfo.directoryRegex.test(directory)
    ) {
      candidate = bvType;
      break;
    }
  }

  if (!candidate) return null;

  // Extract infos from directoryRegex capture groups
  const candidateInfos = BRAINVISA_HIERARCHY[candidate];
  const groups = directory.match(candidateInfos.directoryRegex);
  if (!groups) return null;

  const infos: Record<string, string> = {};
  for (let i = 0; i < candidateInfos.infos.length; i++) {
    infos[candidateInfos.infos[i]] = groups[i + 1];
  }

  // Old project requires acquisition or acquisition_test/reference + subject
  if (
    (!infos.acquisition &&
      !infos.acquisition_test &&
      !infos.acquisition_reference) ||
    !infos.subject
  ) {
    console.warn(
      `[NIfTI Discovery] Subject or acquisition not found in path: ${relativePath}`,
    );
    return null;
  }

  return { infos, bv_type: candidate };
}

function matchGadoliniumFile(relativePath: string): {
  entry: NiftiFileEntry;
  infos: Record<string, string>;
} | null {
  const base = relativePath.match(
    /^(?<subject>[^/]+)\/(?<acquisition>ses-[^/]+)\/t1gadomri\/nifti-prepare\/preprocessed\/native_space\/(?<filename>[^/]+)_reset_downsampled\.nii(?:\.gz)?$/,
  );
  if (base) {
    return {
      entry: {
        setType: "Gadolinium T1",
        directory: "",
        directoryRegex: /./,
        infos: ["subject", "acquisition"],
        pattern: base.groups!.filename,
        patternRegex: /./,
        role: "base",
        modality: "t1gadomri",
        isViewerRelevant: true,
        roiGroups: [],
      },
      infos: {
        subject: base.groups!.subject,
        acquisition: base.groups!.acquisition,
      },
    };
  }
  const mask = relativePath.match(
    /^(?<subject>[^/]+)\/(?<acquisition>ses-[^/]+)\/t1gadomri\/gadolinium-ms-lesions\/segmentation\/native_space\/(?<filename>[^/]+)_segm-gd-enhanced-lesions\.nii(?:\.gz)?$/,
  );
  if (!mask) return null;
  return {
    entry: {
      setType: "Gadolinium lesion mask",
      directory: "",
      directoryRegex: /./,
      infos: ["subject", "acquisition"],
      pattern: mask.groups!.filename,
      patternRegex: /./,
      role: "segmentation",
      modality: "t1gadomri",
      isViewerRelevant: true,
      roiGroups: [],
    },
    infos: {
      subject: mask.groups!.subject,
      acquisition: mask.groups!.acquisition,
    },
  };
}

// ═══════════════════════════════════════════════════════════════
//   Main discovery function
// ═══════════════════════════════════════════════════════════════

export async function discoverNiftiFiles(
  baseUrl: string,
  patientId: string,
  acquisition: string,
): Promise<DiscoveryResult> {
  // Step 1: Fetch file list
  const allPaths = await fetchDiskItems(baseUrl, patientId, acquisition);
  // Step 2: Match each file (replica of parseDiskItems)
  const allFiles: DiscoveredFile[] = [];
  const byKey = new Map<string, DiscoveredFile>();
  let commonInfos: Record<string, string> = {};

  for (const rawPath of allPaths) {
    // disk_items API path format: /data/{orgId}/{patientId}/{subject}/...
    // Strip /data/{orgId}/{patientId}/ prefix to get relative path
    const prefixMatch = rawPath.match(/^\/data\/\d+\/\d+\//);
    const relativePath = prefixMatch
      ? rawPath.slice(prefixMatch[0].length)
      : rawPath.replace(/^\//, "");

    const pet = matchPetFile(relativePath);
    const gadolinium = matchGadoliniumFile(relativePath);
    const parsed = pet
      ? { infos: pet.infos, bv_type: pet.entry.setType }
      : gadolinium
      ? { infos: gadolinium.infos, bv_type: gadolinium.entry.setType }
      : parseDiskItemType(relativePath);
    if (!parsed) continue;

    // Find the corresponding entry in NIFTI_FILE_REGISTRY (with role/modality etc.)
    const entry =
      pet?.entry ??
      gadolinium?.entry ??
      NIFTI_FILE_REGISTRY.find((e) => e.setType === parsed.bv_type);
    if (!entry) continue;

    //  Build loading URL
    const url = `${baseUrl}${transformPath(rawPath)}`;

    const discovered: DiscoveredFile = {
      entry,
      url,
      rawPath,
      infos: parsed.infos,
    };

    allFiles.push(discovered);
    byKey.set(parsed.bv_type, discovered);

    // Collect infos
    if (Object.keys(commonInfos).length === 0) {
      commonInfos = { ...parsed.infos };
    }
  }

  // Step 3: Categorize — filter by current acquisition
  let bases = allFiles
    .filter(
      (f) => f.entry.role === "base" && f.infos.acquisition === acquisition,
    )
    .sort((a, b) => {
      const aIsNative = a.rawPath.includes("native_space") ? 0 : 1;
      const bIsNative = b.rawPath.includes("native_space") ? 0 : 1;
      return (
        aIsNative - bIsNative ||
        a.entry.setType.localeCompare(b.entry.setType) ||
        a.url.localeCompare(b.url)
      );
    });

  // Comparison-type files (e.g. WHASA 4D Labels / longitudinal) capture
  // acquisition_test / acquisition_reference instead of acquisition. Surface
  // them when the current acquisition is EITHER side of the comparison.
  const matchAcq = (f: DiscoveredFile) => {
    const dateStr = acquisition.replace("ses-", "");

    return (
      f.infos.acquisition === acquisition ||
      f.infos.acquisition_test === acquisition ||
      f.infos.acquisition_reference === acquisition ||
      (f.infos.acquisition && f.infos.acquisition.includes(dateStr)) ||
      (f.infos.acquisition_test &&
        f.infos.acquisition_test.includes(dateStr)) ||
      (f.infos.acquisition_reference &&
        f.infos.acquisition_reference.includes(dateStr))
    );
  };

  let segmentations = allFiles.filter(
    (f) =>
      (f.entry.role === "segmentation" || f.entry.role === "atlas") &&
      matchAcq(f),
  );

  const hasPet = bases.some((b) => isPetModality(b.entry.modality));

  if (hasPet) {
    bases = bases.filter((b) => isPetModality(b.entry.modality));
    segmentations = segmentations.filter((s) =>
      isPetModality(s.entry.modality),
    );
  }

  const activeModality: Modality = hasPet
    ? bases[0].entry.modality!
    : bases.some((b) => b.entry.modality === "t1mri")
    ? "t1mri"
    : bases.some((b) => b.entry.modality === "t2flairmri")
    ? "t2flairmri"
    : bases[0]?.entry.modality ?? "t1mri";

  // Debug: log unmatched NIfTI files
  const niftiPaths = allPaths.filter(
    (p) => p.endsWith(".nii.gz") || p.endsWith(".nii"),
  );
  const matchedRawPaths = new Set(allFiles.map((f) => f.rawPath));
  const unmatched = niftiPaths.filter((p) => !matchedRawPaths.has(p));
  if (unmatched.length > 0) {
    console.warn(
      `[NIfTI Discovery] ⚠️ ${unmatched.length} unmatched NIfTI files:`,
      unmatched,
    );
  }

  return {
    bases,
    segmentations,
    activeModality,
    byKey,
    allFiles,
    infos: commonInfos,
  };
}
